import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { capitalizer, formatAmount, formatPercentage, truncate } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onComparatorTradedAmountsThunk } from './ComparatorTradedAmounts.actions'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import LineChart from '../../components/Charts/LineChart'
import ComparadorTradedAmounts from '../../components/Infographics/ComparadorTradedAmounts'
import { Wrapper, GridWrapper, Link } from './ComparatorTradedAmounts.styles'

/**
 * The ComparatorTradedAmounts' container.
 */
const ComparatorTradedAmounts = ({ firstEntity, secondEntity, latestUpdate }) => {
  const dispatch = useDispatch()
  const inputLineChartRef = useRef()
  const dataTableRef = useRef()
  const { endYear, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Montos_Transados_${years[0].value}`)
  const {
    data: { chartStructure, csvStructure, categories },
    fetching: loading
  } = useSelector(state => state.comparatorTradedAmount)
  const handleShowTable = updatedValue => setShowTable(updatedValue)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onComparatorTradedAmountsThunk(
          { year: updatedValue, idEntity: [firstEntity.value, secondEntity.value] },
          distValue
        )
      )
      setFileName(`Montos_Transados_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleChangeDist } = useSelect({
    initialValue: 'amount',
    changeCallback: updatedValue => {
      dispatch(
        onComparatorTradedAmountsThunk(
          { year: yearValue, idEntity: [firstEntity.value, secondEntity.value] },
          updatedValue
        )
      )
    }
  })
  const distribution = [
    { name: 'Mes del año', value: 'amount' },
    { name: 'Acumulativo anual', value: 'totalAmount' },
    { name: 'Monto Porcentual Mensual', value: 'percentageMonth' },
    { name: 'Monto Porcentual Acumulado', value: 'percentageAccumulative' }
  ]

  const handleCaptureImage = () => exportToImage('ComparatorTradedAmounts', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    dispatch(
      onComparatorTradedAmountsThunk(
        { year: years[0].value, idEntity: [firstEntity.value, secondEntity.value] },
        'amount'
      )
    )
  }, [firstEntity, secondEntity])

  return (
    <Wrapper margin="64px auto 0">
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Montos transados
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            A continuación podrás revisar la evolución de los montos transados por los organismos compradores según el
            año seleccionado. Recuerda que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={10} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearValue} onChange={handleChangeYear} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleChangeDist} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            singleIcon
            disabled={!showTable}
            margin="24px 10px 0 0"
            type="toggleChart"
            variant="outlined"
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            type="toggleTable"
            variant="outlined"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      {showTable ? (
        <GridWrapper container justifyContent="center" margin="24px 0 0">
          <GridWrapper item xs={12}>
            <DataTable
              headers={[
                { label: 'Organismo comprador', value: 0 },
                { label: 'Año', value: 1 },
                { label: 'Mes del año', value: 2 },
                { label: 'Monto transado', value: 3 },
                { label: 'Monto acumulado', value: 4 },
                { label: 'Monto porcentual mensual', value: 5 },
                { label: 'Monto porcentual acumulado', value: 6 }
              ]}
              headersCellsProps={[
                { enableSort: true },
                null,
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[
                ([entity]) => truncate(capitalizer(entity), 40),
                ([, year]) => year,
                ([, , month]) => month,
                ([, , , amount]) => `$${formatAmount(amount)}`,
                ([, , , , totalAmount]) => `$${formatAmount(totalAmount)}`,
                ([, , , , , percentage]) => formatPercentage(percentage),
                ([, , , , , , totalPercentage]) => formatPercentage(totalPercentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        </GridWrapper>
      ) : (
        <GridWrapper container justifyContent="center">
          <GridWrapper item xs={12} margin="24px 0 0">
            <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" />
            <LineChart
              categories={categories}
              distribution={distValue}
              fileName={fileName}
              info={chartStructure}
              inputLineChartRef={inputLineChartRef}
              year={yearValue}
              id="ComparatorTradedAmounts"
            />
          </GridWrapper>
        </GridWrapper>
      )}
      <GridWrapper container justifyContent="center" margin="40px 0 0">
        <GridWrapper item xs={12}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<ComparadorTradedAmounts />}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            latestUpdate={latestUpdate}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

ComparatorTradedAmounts.propTypes = {
  firstEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  secondEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  latestUpdate: PropTypes.string
}

export default ComparatorTradedAmounts
